import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ImageIcon, Upload, Trash2, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminMediaPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/media`);
      setItems(data || []);
    } catch {
      toast.error("Failed to load media library.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const fullUrl = (item) => (item.url.startsWith("http") ? item.url : `${BACKEND}${item.url}`);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error("File is too large (max 6 MB).");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const { data } = await axios.post(`${API}/admin/media`, form);
      toast.success("Uploaded!");
      setItems((prev) => [data, ...prev]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete ${item.filename}?`)) return;
    try {
      await axios.delete(`${API}/admin/media/${item.id}`);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      toast.success("Deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div data-testid="admin-media-page" className="bg-white min-h-full">
      <div className="px-5 lg:px-8 py-5 border-b border-slate-200 flex items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-slate-900">
            <ImageIcon className="h-4 w-4 text-sky-600" />
            <h1 className="text-base font-bold">Media Library</h1>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {items.length} item(s) - max 6 MB per upload (PNG, JPG, WEBP, GIF, SVG)
          </p>
        </div>
        <label
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold bg-sky-600 hover:bg-sky-700 text-white cursor-pointer ${
            uploading ? "opacity-60 cursor-wait" : ""
          }`}
          data-testid="media-upload-label"
        >
          <Upload className="h-3.5 w-3.5" />
          {uploading ? "Uploading…" : "Upload image"}
          <input
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp,image/gif,image/svg+xml"
            className="hidden"
            onChange={handleUpload}
            disabled={uploading}
            data-testid="media-upload-input"
          />
        </label>
      </div>

      <div className="p-5 lg:p-8">
        {loading ? (
          <p className="text-sm text-slate-400 text-center py-10">Loading…</p>
        ) : items.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-slate-200 rounded-xl bg-slate-50/50">
            <p className="text-sm text-slate-500">No images yet - upload your first one.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map((item) => {
              const url = fullUrl(item);
              return (
                <div
                  key={item.id}
                  data-testid={`media-row-${item.id}`}
                  className="group relative bg-white border border-slate-200 rounded-lg overflow-hidden hover:border-sky-300 transition-colors"
                >
                  <div className="relative w-full aspect-square flex items-center justify-center bg-slate-50">
                    <img
                      src={url}
                      alt={item.alt || item.filename}
                      className="max-w-full max-h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="px-2 py-1.5 flex items-center gap-1 justify-between border-t border-slate-100">
                    <p className="text-[10px] text-slate-500 truncate flex-1" title={item.filename}>
                      {item.filename}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(url);
                        toast.success("URL copied");
                      }}
                      title="Copy URL"
                      className="text-slate-400 hover:text-sky-600 p-0.5"
                    >
                      <Copy className="h-3 w-3" />
                    </button>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open"
                      className="text-slate-400 hover:text-sky-600 p-0.5"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </a>
                    <button
                      type="button"
                      onClick={() => handleDelete(item)}
                      title="Delete"
                      className="text-slate-400 hover:text-red-500 p-0.5"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
